import moment from 'moment';

import { ApiRequestService } from 'services/ApiRequestService';

const microservice = 'employee';
const harnessRunEndpoint = 'RulesEngine/HarnessRun';
const harnessRunErrorMessage = "Could not run the rules engine. Please try again.";
// A long range can take well over the 30 second default to analyse.
const harnessRunConfig = { timeout: 60000 };
const infringementDiffsEndpoint = 'InfringementDiffs';
const infringementDiffGroupsEndpoint = 'InfringementDiffs/Groups';
const infringementDiffCoverageEndpoint = 'InfringementDiffs/Coverage';
const infringementDiffGroupSignOffEndpoint = 'InfringementDiffs/Groups/SignOff';
const infringementDiffDailyCountsEndpoint = 'InfringementDiffs/DailyCounts';
const personSourceId = 1;
const underTestComparisonEndpoint = 'RulesEngine/UnderTestComparison';
// The clear-down removes every UnderTest row in one transaction, which can outlast the 30 second default.
const clearDownConfig = { timeout: 60000 };
const isoDateFormat = 'YYYY-MM-DD';

// Unset filters are left off the query, so a view with no period chosen is sent no period.
function buildSentParameters(parameters) {
    return Object.fromEntries(Object.entries(parameters)
        .filter(([, value]) => value != null && value !== '' && value !== false));
}

function buildQueryString(parameters) {
    const pairs = Object.entries(buildSentParameters(parameters))
        .map(([name, value]) => `${name}=${encodeURIComponent(value)}`);

    if (pairs.length === 0) {
        return '';
    }

    return `?${pairs.join('&')}`;
}

// The diff viewer's filter vocabulary, translated to the values the endpoint takes.
const wireComparisonModes = {
    "legacy-v-shadow": "LegacyVersusShadow",
    "under-test": "UnderTest"
};

const wireLabels = {
    "untriaged": "Untriaged",
    "known-difference": "KnownDifference",
    "regression": "Regression"
};

function toWireValue(wireValues, value) {
    if (value == null) {
        return null;
    }

    return wireValues[value] ?? value;
}

// The viewer's period ends on a date it includes; the endpoint's period ends on the instant it excludes.
function toExclusiveEnd(inclusiveEndDate) {
    if (inclusiveEndDate == null) {
        return null;
    }

    return moment(inclusiveEndDate, isoDateFormat).add(1, 'days').format(isoDateFormat);
}

function buildDiffParameters(filters) {
    return {
        comparisonMode: toWireValue(wireComparisonModes, filters.mode),
        from: filters.from,
        to: toExclusiveEnd(filters.to),
        infringementTypeId: filters.typeId,
        label: toWireValue(wireLabels, filters.label),
        signature: filters.signature,
        employeeId: filters.employeeId,
        includeProvisional: filters.includeProvisional,
        includeWhitelisted: filters.includeWhitelisted,
        includeSignedOff: filters.includeSignedOff
    };
}

// Rejects with the axios response so the caller can tell a 400, which carries the API's message,
// from any other failure.
function getWithQuery(endpoint, parameters) {
    return new Promise((resolve, reject) => {
        ApiRequestService.get(microservice, `${endpoint}${buildQueryString(parameters)}`).then(
            (result) => {
                resolve(result);
            },
            (error) => {
                reject(error?.response);
            });
    });
}

// Posted without an error message, so ApiRequestService rejects with the axios response (none after a
// timeout) and the caller can still tell a 400 from any other failure.
function postForResponse(endpoint, body, config) {
    return new Promise((resolve, reject) => {
        ApiRequestService.post(microservice, endpoint, body, undefined, config).then(
            (result) => {
                resolve(result);
            },
            (error) => {
                reject(error);
            });
    });
}

export const RulesEngineService = {

    // Runs the engine for a driver and date range and returns the full working set without writing
    // anything, so an operator exploring the harness cannot move the shadow comparison's diff rate.
    runHarness(request) {
        return new Promise((resolve, reject) => {
            ApiRequestService.post(microservice, harnessRunEndpoint, request, harnessRunErrorMessage, harnessRunConfig).then(
                (result) => {
                    resolve(result);
                },
                (error) => {
                    reject(error);
                });
        });
    },

    getInfringementDiffs(filters) {
        return getWithQuery(infringementDiffsEndpoint, buildDiffParameters(filters));
    },

    // The query getInfringementDiffs sends for these filters, as the endpoint receives it.
    buildInfringementDiffQuery(filters) {
        return buildSentParameters(buildDiffParameters(filters));
    },

    /**
     * The summary and the groups for a set of filters. The type choices come from a groups read that neither the type
     * nor the label narrows, so choosing one never drops the others; with neither set, the groups read serves both.
     */
    getDiffViewerReads(filters) {
        const groupsRead = getWithQuery(infringementDiffGroupsEndpoint, buildDiffParameters({ ...filters, signature: null }));
        let typeGroupsRead = groupsRead;

        if (filters.typeId != null || filters.label != null) {
            typeGroupsRead = getWithQuery(infringementDiffGroupsEndpoint, buildDiffParameters({ ...filters, typeId: null, label: null, signature: null }));
        }

        // The type choices are a convenience: without them the gate still shows, and the groups read reports
        // its own failure, so the summary does not report it again.
        const typeGroupsOrNone = new Promise((resolve) => {
            typeGroupsRead.then(
                (result) => {
                    resolve(result);
                },
                () => {
                    resolve(null);
                });
        });

        const summary = new Promise((resolve, reject) => {
            Promise.all([
                getWithQuery(infringementDiffsEndpoint, buildDiffParameters({ ...filters, signature: null })),
                typeGroupsOrNone
            ]).then(
                ([differences, typeGroups]) => {
                    resolve({ differences: differences, typeGroups: typeGroups });
                },
                (error) => {
                    reject(error);
                });
        });

        const groups = new Promise((resolve, reject) => {
            groupsRead.then(
                (result) => {
                    resolve(result);
                },
                (error) => {
                    reject(error);
                });
        });

        return { summary: summary, groups: groups };
    },

    getInfringementDiffCoverage(from, to) {
        return getWithQuery(infringementDiffCoverageEndpoint, { from: from, to: toExclusiveEnd(to) });
    },

    // The actor is taken from the token, so no user is sent. The evidence hash goes back exactly as the read
    // returned it, so the endpoint can refuse a judgement on a difference that has changed since.
    signOffInfringementDiff(id, judgement, evidenceHash) {
        return postForResponse(`${infringementDiffsEndpoint}/${id}/SignOff`, {
            labelId: judgement.labelId,
            reason: judgement.reason,
            workItemId: judgement.workItemId,
            sourceId: personSourceId,
            evidenceHash: evidenceHash
        });
    },

    /**
     * Signs off every open difference in a group under the filters its rows are read with. The endpoint leaves
     * provisional differences out unless told otherwise, so the choice is always sent. It refuses the sign-off
     * unless the group still holds the expected count of open differences.
     */
    signOffInfringementDiffGroup(filters, signature, judgement, expectedCount) {
        const parameters = buildDiffParameters(filters);

        return postForResponse(infringementDiffGroupSignOffEndpoint, {
            comparisonMode: parameters.comparisonMode,
            signature: signature,
            employeeId: parameters.employeeId,
            infringementTypeId: parameters.infringementTypeId,
            from: parameters.from,
            to: parameters.to,
            includeProvisional: filters.includeProvisional === true,
            filterLabel: parameters.label,
            expectedCount: expectedCount,
            labelId: judgement.labelId,
            reason: judgement.reason,
            workItemId: judgement.workItemId
        });
    },

    /**
     * Unlike the other reads, the daily counts take UK-local dates and include the last one. The endpoint
     * includes provisional differences unless told otherwise, so the choice is always sent, false included.
     */
    getInfringementDiffDailyCounts(request) {
        return getWithQuery(infringementDiffDailyCountsEndpoint, {
            comparisonMode: toWireValue(wireComparisonModes, request.mode),
            from: request.from,
            to: request.to,
            includeProvisional: request.includeProvisional === true ? 'true' : 'false'
        });
    },

    getUnderTestComparison() {
        return getWithQuery(underTestComparisonEndpoint, {});
    },

    startUnderTestComparison(parentPbiId) {
        return postForResponse(`${underTestComparisonEndpoint}/Start`, { parentPbiId: parentPbiId });
    },

    stopUnderTestComparison() {
        return postForResponse(`${underTestComparisonEndpoint}/Stop`);
    },

    // Resolves with what was removed: { differencesRemoved, signOffsRemoved, retainedRequestsRemoved }.
    clearDownUnderTestComparison() {
        return postForResponse(`${underTestComparisonEndpoint}/ClearDown`, undefined, clearDownConfig);
    }
};
